/**
 * useNodeConnectionStatus Hook
 *
 * Provides connection validation status for a workflow node.
 * Used by canvas nodes to display connection error badges.
 */

import { useMemo } from 'react';
import { useConnectionStore } from '@/stores/connectionStore';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import type { ConnectionReference, ConnectionReferences } from '@/types/connectionReference';
import {
  createValidResult,
  createWarningResult,
  createMockActiveResult,
  createReferenceNotFoundResult,
  operationRequiresConnection,
  type ConnectionValidationResult,
  createMissingConnectionError,
} from '@/types/connectionValidation';
import { getBuiltInManifest } from '@/data/manifests';
import { requiresConnection } from '@/types/operationManifest';
import { useWorkflowStore } from '@/stores/workflowStore';
import { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';

/**
 * Resolve a reference key against the store with a case-insensitive fallback.
 * Workflow JSON and ARM resource names sometimes drift in casing (e.g. user
 * typed displayName "ericTest" but earlier code lowercased the store key to
 * "erictest"); without this fallback those nodes show a spurious
 * "reference not found" error.
 */
function lookupReference(
  references: ConnectionReferences,
  key: string,
): ConnectionReference | undefined {
  const exact = references[key];
  if (exact) return exact;
  const lower = key.toLowerCase();
  for (const [candidateKey, ref] of Object.entries(references)) {
    if (candidateKey.toLowerCase() === lower) return ref;
  }
  return undefined;
}

/**
 * Options for the useNodeConnectionStatus hook
 */
export interface UseNodeConnectionStatusOptions {
  /** Node ID in the workflow */
  nodeId: string;
  /** Operation type (e.g., 'Http', 'Agent', 'ServiceBus') */
  operationType: string;
  /** Override whether connection is required (from manifest) */
  connectionRequired?: boolean;
  /** Connection reference key from the workflow (e.g., from serviceProviderConfiguration.connectionName) */
  connectionReferenceKey?: string | undefined;
}

/**
 * Return type for the useNodeConnectionStatus hook
 */
export interface NodeConnectionStatusResult {
  /** Validation result for display */
  validationResult: ConnectionValidationResult | null;
  /** Whether the node has a connection assigned */
  hasConnection: boolean;
  /** Whether a connection is required for this node */
  isRequired: boolean;
  /** The reference key if assigned */
  referenceKey: string | null;
  /** Connection display name if available */
  connectionName: string | null;
}

/**
 * Hook to get connection validation status for a node
 *
 * @param options - Configuration options
 * @returns Connection status result
 *
 * Usage:
 * const status = useNodeConnectionStatus(options);
 * Then use status.validationResult with ConnectionStatusBadge
 */
export function useNodeConnectionStatus({
  nodeId,
  operationType,
  connectionRequired,
  connectionReferenceKey,
}: UseNodeConnectionStatusOptions): NodeConnectionStatusResult {
  // Get connection state from store
  // Prefer the passed connectionReferenceKey (from workflow data), fallback to mapping
  const mappedKey = useConnectionStore((state) => state.mapping[nodeId] ?? null);
  const references = useConnectionStore((state) => state.references);

  // Mock data state (no longer gated by a "template mode").
  const mockedNodes = useWorkflowStore((state) => state.mockedNodes);

  // Use the explicit reference key if provided, otherwise use mapped key
  const referenceKey = connectionReferenceKey ?? mappedKey;

  return useMemo(() => {
    // A node is "mocked" when it has canned output and its per-action toggle
    // is on (enabled !== false). The mocked status takes precedence over any
    // connection error so the badge reads "Mocked" while the action is mocked.
    const nodeMockData = mockedNodes[nodeId];
    const isActionNode = (nodeMockData as MockNodeInfo)?.actionName !== undefined;
    const hasMockData = isActionNode
      ? !!(nodeMockData as MockNodeInfo).mockOutput && (nodeMockData as MockNodeInfo).enabled !== false
      : !!nodeMockData && (nodeMockData as TriggerMockPayload).enabled !== false;
    // Determine if connection is required
    // Priority: explicit override > manifest > type-based fallback
    let isRequired = connectionRequired;
    if (isRequired === undefined) {
      const manifest = getBuiltInManifest(operationType);
      if (manifest?.properties.connection !== undefined) {
        isRequired = requiresConnection(manifest);
      } else {
        isRequired = operationRequiresConnection(operationType);
      }
    }

    // Mocked nodes win over EVERY connection status: a mocked action returns
    // its canned output instead of running live, so the badge reads "Mocked"
    // regardless of whether the connection is required, missing, incomplete,
    // or fully configured. We still resolve the reference (when present) so the
    // connector identity stays available to the canvas.
    if (hasMockData) {
      const mockRef = referenceKey ? lookupReference(references, referenceKey) : undefined;
      return {
        validationResult: createMockActiveResult(),
        hasConnection: mockRef ? isConnectionReferenceComplete(mockRef) : false,
        isRequired: !!isRequired,
        referenceKey: referenceKey ?? null,
        connectionName: mockRef?.connectionName ?? referenceKey ?? null,
      };
    }

    // If no connection required, no badge needed.
    if (!isRequired) {
      return {
        validationResult: null,
        hasConnection: false,
        isRequired: false,
        referenceKey: null,
        connectionName: null,
      };
    }

    // Check if connection is assigned
    if (!referenceKey) {
      // Global change: missing connection → warning (not error)
      return {
        validationResult: createWarningResult('Connection not yet configured'),
        hasConnection: false,
        isRequired: true,
        referenceKey: null,
        connectionName: null,
      };
    }

    // Check if reference exists in connections
    const reference = lookupReference(references, referenceKey);
    if (!reference) {
      return {
        validationResult: createReferenceNotFoundResult(referenceKey),
        hasConnection: false,
        isRequired: true,
        referenceKey,
        connectionName: null,
      };
    }

    if (!isConnectionReferenceComplete(reference)) {
      return {
        validationResult: createMissingConnectionError('Connection configuration invalid or incomplete'),
        hasConnection: false,
        isRequired: true,
        referenceKey,
        connectionName: reference.connectionName ?? referenceKey,
      };
    }

    // Connection is valid
    return {
      validationResult: createValidResult(),
      hasConnection: true,
      isRequired: true,
      referenceKey,
      connectionName: reference.connectionName ?? referenceKey,
    };
  }, [nodeId, operationType, connectionRequired, referenceKey, references, mockedNodes]);
}

/**
 * Hook to get connection validation status for multiple nodes
 * Useful for batch validation or canvas-level error display
 *
 * @param nodeIds - Array of node configurations
 * @returns Map of node ID to validation results
 */
export function useMultiNodeConnectionStatus(
  nodes: Array<{ nodeId: string; operationType: string; connectionRequired?: boolean }>
): Map<string, NodeConnectionStatusResult> {
  const mapping = useConnectionStore((state) => state.mapping);
  const references = useConnectionStore((state) => state.references);

  return useMemo(() => {
    const results = new Map<string, NodeConnectionStatusResult>();

    for (const { nodeId, operationType, connectionRequired } of nodes) {
      const isRequired = connectionRequired ?? operationRequiresConnection(operationType);

      if (!isRequired) {
        results.set(nodeId, {
          validationResult: null,
          hasConnection: false,
          isRequired: false,
          referenceKey: null,
          connectionName: null,
        });
        continue;
      }

      const referenceKey = mapping[nodeId] ?? null;

      if (!referenceKey) {
        // Global change: missing connection → warning (not error)
        results.set(nodeId, {
          validationResult: createWarningResult('Connection not yet configured'),
          hasConnection: false,
          isRequired: true,
          referenceKey: null,
          connectionName: null,
        });
        continue;
      }

      const reference = lookupReference(references, referenceKey);
      if (!reference) {
        results.set(nodeId, {
          validationResult: createReferenceNotFoundResult(referenceKey),
          hasConnection: false,
          isRequired: true,
          referenceKey,
          connectionName: null,
        });
        continue;
      }

      results.set(nodeId, {
        validationResult: createValidResult(),
        hasConnection: true,
        isRequired: true,
        referenceKey,
        connectionName: reference.connectionName ?? referenceKey,
      });
    }

    return results;
  }, [nodes, mapping, references]);
}

/**
 * Hook to check if any nodes have connection errors
 * Useful for showing workflow-level validation warnings
 */
export function useHasConnectionErrors(): boolean {
  const mapping = useConnectionStore((state) => state.mapping);
  const references = useConnectionStore((state) => state.references);

  return useMemo(() => {
    for (const [, refKey] of Object.entries(mapping)) {
      if (refKey && !references[refKey]) {
        return true; // Reference not found
      }
    }
    return false;
  }, [mapping, references]);
}

/**
 * Hook to get count of nodes with connection errors
 */
export function useConnectionErrorCount(): number {
  const mapping = useConnectionStore((state) => state.mapping);
  const references = useConnectionStore((state) => state.references);

  return useMemo(() => {
    let count = 0;
    for (const [, refKey] of Object.entries(mapping)) {
      if (refKey && !references[refKey]) {
        count++;
      }
    }
    return count;
  }, [mapping, references]);
}
